import React, { useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import moment from "moment";

// Optional: keep same header style you use
const Header = ({ title, subtitle, align = "center" }) => (
  <Box sx={{ width: "100%", textAlign: align, display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
    <Typography sx={{ fontSize: 12, fontWeight: 700 }}>{title}</Typography>
    {subtitle && <Typography sx={{ fontSize: 11, fontWeight: 500, opacity: 0.8 }}>{subtitle}</Typography>}
  </Box>
);

const formatLakhs = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  return Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 });
};

const formatDate = (d) => {
  if (!d) return "—";
  const m = moment(d);
  return m.isValid() ? m.format("DD MMM YYYY") : String(d);
};

// Makes status look nice (✅ / Under Process / Target Closure etc)
const StatusChip = ({ value }) => {
  if (!value) return <Typography sx={{ fontSize: 12, color: "#6B7280" }}>—</Typography>;

  const text = String(value).trim();

  if (text === "✅") return <Chip size="small" label="Done" sx={{ fontSize: 12 }} />;
  if (/under process/i.test(text)) return <Chip size="small" label="Under Process" sx={{ fontSize: 12 }} />;
  if (/ongoing/i.test(text)) return <Chip size="small" label="Ongoing" sx={{ fontSize: 12 }} />;
  if (/yet to start/i.test(text)) return <Chip size="small" label="Yet to start" sx={{ fontSize: 12 }} />;
  if (/target/i.test(text)) return <Chip size="small" label={text.replace(/\s+/g, " ")} sx={{ fontSize: 12 }} />;

  // fallback: plain text
  return (
    <Typography sx={{ fontSize: 12, whiteSpace: "pre-line" }}>
      {text}
    </Typography>
  );
};

const BusinessPipelineTable = ({
  // Replace these with your RTK query outputs later
  rows = [],
  isFetching = false,
  isError = false,
  asOnDate = "2026-02-27", // you can pass from API/status date
}) => {
  const [search, setSearch] = useState("");

  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) =>
      (r.name || "").toLowerCase().includes(q) ||
      (r.location || "").toLowerCase().includes(q) ||
      (r.type || "").toLowerCase().includes(q)
    );
  }, [rows, search]);

  // Example columns matching your Excel
  const columns = useMemo(
    () => [
      { key: "priority", header: <Header title="Priority" />, width: 70, align: "center" },
      { key: "name", header: <Header title="Name" align="left" />, width: 220, align: "left" },
      { key: "location", header: <Header title="Location" align="left" />, width: 100, align: "left" },
      { key: "type", header: <Header title="Type" />, width: 50, align: "center" },
      {
        key: "area",
        header: <Header title="Builtup Area (Sqft)" subtitle="Carpet Area (Sqft)" />,
        width: 100,
        align: "center",
      },
      { key: "capex", header: <Header title="Capex" subtitle="(INR Lakhs)" />, width: 80, align: "center" },
      { key: "opex", header: <Header title="Opex" subtitle="(INR Lakhs)" />, width: 80, align: "center" },
      { key: "loiSigning", header: <Header title="LOI" subtitle="Signing" />, width: 120, align: "center" },
      { key: "agreementSigning", header: <Header title="Agreement" subtitle="Signing" />, width: 140, align: "center" },
      { key: "tokenRelease", header: <Header title="Token Amount" subtitle="Release" />, width: 140, align: "center" },
      { key: "execution", header: <Header title="Project" subtitle="Execution" />, width: 130, align: "center" },
      { key: "leasing", header: <Header title="Leasing" />, width: 240, align: "left" },
      { key: "icApproved", header: <Header title="IC Approved" subtitle="Operation Start" />, width: 140, align: "center" },
    ],
    []
  );

  return (
    <Grid container spacing={2} sx={{ width: "100%", boxSizing: "border-box" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, fontFamily: "Outfit", mb: 1.5 }}>
          Business Pipeline
          <Typography component="span" sx={{ ml: 1, fontSize: 13, fontWeight: 400, color: "#6B7280", display: "inline" }}>
            As on {formatDate(asOnDate)}
          </Typography>
        </Typography>
      </Box>

      <Grid item xs={12} sx={{ mt: 1, position: "relative" }}>
        {isFetching && (
          <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(255,255,255,0.7)", zIndex: 10 }}>
            <CircularProgress />
          </Box>
        )}

        {isError && (
          <Box sx={{ p: 4, textAlign: "center", color: "red" }}>
            <Typography variant="h6">Error loading data.</Typography>
          </Box>
        )}

        <Box sx={{ display: "flex", justifyContent: "flex-start", mb: 1 }}>
          <TextField
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search name / location / type..."
            size="small"
            sx={{ width: 360 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon sx={{ fontSize: 18, color: "#6B7280" }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseRoundedIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>

        {!isError && (
          <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: "auto" }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1400 }}>
              <TableHead>
                <TableRow>
                  {columns.map((c) => (
                    <TableCell
                      key={c.key}
                      align={c.align}
                      sx={{
                        width: c.width,
                        minWidth: c.width,
                        fontSize: 12,
                        fontWeight: 700,
                        bgcolor: "#F9FAFB",
                        borderBottom: "1px solid #E5E7EB",
                      }}
                    >
                      {c.header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredRows.map((r, idx) => (
                  <TableRow key={r.id ?? idx} hover>
                    <TableCell align="center">{r.priority ?? "—"}</TableCell>
                    <TableCell align="left" sx={{ fontWeight: 600 }}>
                      {r.name ?? "—"}
                    </TableCell>
                    <TableCell align="left" sx={{ whiteSpace: "pre-line" }}>
                      {r.location ?? "—"}
                    </TableCell>
                    <TableCell align="center">{r.type ?? "—"}</TableCell>
                    <TableCell align="center" sx={{ whiteSpace: "pre-line" }}>
                      {r.area ?? "—"}
                    </TableCell>
                    <TableCell align="center">{formatLakhs(r.capex)}</TableCell>
                    <TableCell align="center">{formatLakhs(r.opex)}</TableCell>
                    <TableCell align="center"><StatusChip value={r.loiSigning} /></TableCell>
                    <TableCell align="center"><StatusChip value={r.agreementSigning} /></TableCell>
                    <TableCell align="center"><StatusChip value={r.tokenRelease} /></TableCell>
                    <TableCell align="center"><StatusChip value={r.execution} /></TableCell>
                    <TableCell align="left" sx={{ whiteSpace: "pre-line" }}>{r.leasing ?? "—"}</TableCell>
                    <TableCell align="center">
                      {/* could be a date in excel */}
                      {moment(r.icApproved).isValid() ? formatDate(r.icApproved) : <StatusChip value={r.icApproved} />}
                    </TableCell>
                  </TableRow>
                ))}

                {filteredRows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} sx={{ py: 4, textAlign: "center", color: "#6B7280" }}>
                      No records found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Grid>
    </Grid>
  );
};

export default BusinessPipelineTable;
